import {
  Alert,
  Checkbox,
  CustomTooltip,
  Input,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import {
  ClipboardCheckOutlineIcon,
  DragAndDropOutlineIcon,
  Icon,
  LightningBoltOutlineIcon,
  LockClosedOutlineIcon,
  PencilOutlineIcon,
  PhotographOutlineIcon,
  SearchOutlineIcon,
  ShieldCheckOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import {
  ButtonBase,
  IconButton,
  InputBase,
  Typography,
} from '@material-ui/core';
import { StandardQuestionTemplate } from 'api-clients/monolith';
import cx from 'classnames';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { OptionsEditor } from './OptionsEditor';
import { StandardKeyPicker } from './StandardKeyPicker';
import { useStyles } from './styles';
import {
  canConfirmAnswer,
  canValidateAnswer,
  customTypesFor,
  CustomType,
  destroyedLogicJumps,
  EditableQuestion,
  EditorSettings,
  hasDateRange,
  hasLogicJumps,
  IMAGE_ACCEPT,
  IMAGE_MAX_BYTES,
  IMAGE_TYPES,
  isOptionable,
  liveLogicJumps,
  MAX_YEARS_OF_VALIDATION,
  MIN_YEARS_OF_VALIDATION,
  OptionBank,
  QuestionErrors,
  QuestionOption,
  removeLogicJumpValue,
  requiresYearsOfValidation,
  showsFormat,
  syncLogicJumpTriggers,
  templateOptions,
  triggerValuesByIndex,
} from './types';
import { useMenuEscapeGuard } from './useMenuEscapeGuard';

export type LogicConfirmationKind =
  | 'removeQuestion'
  | 'changeType'
  | 'linkBank'
  | 'unlinkBank'
  | 'removeOption';

export interface QuestionCardProps {
  question: EditableQuestion;
  errors?: QuestionErrors;
  templates: StandardQuestionTemplate[];
  optionBanks: OptionBank[];
  settings: EditorSettings;
  usedKeys: string[];
  canRestrict: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onChange: (patch: Partial<EditableQuestion>) => void;
  onRemove: () => void;
  /**
   * Asks before an edit that drops a logic jump; `onConfirm` runs only when
   * the person agrees.
   */
  requestConfirmation: (
    kind: LogicConfirmationKind,
    onConfirm: () => void,
  ) => void;
}

const TYPE_MESSAGES: Record<CustomType, keyof typeof messages> = {
  text_field: 'typeTextField',
  text_area: 'typeTextArea',
  radio: 'typeRadio',
  checkboxes: 'typeCheckboxes',
  dropdown: 'typeDropdown',
  datepicker: 'typeDatepicker',
  file: 'typeFile',
  resume: 'typeResume',
  address: 'typeAddress',
  address_history: 'typeAddressHistory',
  employment_history: 'typeEmploymentHistory',
  history_address: 'typeHistoryAddress',
  history_employment: 'typeHistoryEmployment',
  hidden_field: 'typeHiddenField',
};

/** The fields a standard key overwrites, which undo puts back. */
type Binding = Pick<
  EditableQuestion,
  | 'key'
  | 'type'
  | 'predefined'
  | 'question'
  | 'options'
  | 'option_bank_id'
  | 'restricted'
  | 'secure'
  | 'format'
  | 'format_validation_message'
  | 'min_date'
  | 'max_date'
  | 'logic_jumps_attributes'
>;

type UndoableBinding = {
  previous: Binding;
  previousCustomKeyMode: boolean;
  /** The wording the key left in the field; undo keeps any later edit. */
  seededQuestion: string;
  title: string;
};

/** File questions are always restricted; the key carries the flag on the server. */
const ALWAYS_RESTRICTED = ['file', 'avatar', 'resume'];

/** The validation settings a change of answer type leaves behind. */
const CLEARED_VALIDATION: Partial<EditableQuestion> = {
  format: null,
  format_validation_message: null,
  min_date: null,
  max_date: null,
};

export const QuestionCard: FC<QuestionCardProps> = ({
  question,
  errors,
  templates,
  optionBanks,
  settings,
  usedKeys,
  canRestrict,
  dragHandleProps,
  onChange,
  onRemove,
  requestConfirmation,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [customKeyMode, setCustomKeyMode] = useState(
    !question.predefined && Boolean(question.key),
  );
  const [showHint, setShowHint] = useState(Boolean(question.hint));
  const [showDateRange, setShowDateRange] = useState(hasDateRange(question));
  // The jump trigger each option currently holds, by position: a trigger
  // follows its option through every keystroke of a rename, blank ones
  // included, and never jumps to another option that passes through its text.
  const [triggers, setTriggers] = useState<Record<number, string>>(() =>
    triggerValuesByIndex(question, question.options ?? []),
  );
  const [imageError, setImageError] = useState<string | null>(null);
  const [undoable, setUndoable] = useState<UndoableBinding | null>(null);
  // Undo unmounts the button that holds focus. The key control it restores
  // exists only after the next render, so focus moves from an effect.
  const [refocusKey, setRefocusKey] = useState(false);
  const keyButtonRef = useRef<HTMLButtonElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const typeMenuGuard = useMenuEscapeGuard();

  useEffect(() => {
    if (!refocusKey) return;
    (customKeyMode ? keyInputRef : keyButtonRef).current?.focus();
    setRefocusKey(false);
  }, [refocusKey, customKeyMode]);

  const template = question.predefined
    ? templates.find(t => t.key === question.key)
    : undefined;

  const typeLabel = (type: string) => {
    const messageKey = TYPE_MESSAGES[type as CustomType];
    return messageKey
      ? intl.formatMessage(messages[messageKey])
      : intl.formatMessage(messages.typeOther);
  };

  const typeOptions = customTypesFor(settings).map(type => ({
    value: type,
    label: typeLabel(type),
  }));
  const currentType = { value: question.type, label: typeLabel(question.type) };

  // The canonical text seeds the field so the recruiter edits a real question
  // rather than a blank line. Wording they typed themselves always survives;
  // only an empty field or the previous standard question's own canonical text
  // is replaced, so switching keys never strands the wrong default.
  const seededQuestion = (chosen: StandardQuestionTemplate) => {
    const current = question.question.trim();
    const untouched = current === '' || current === template?.question.trim();
    return untouched ? chosen.question : question.question;
  };

  const applyTemplate = (chosen: StandardQuestionTemplate) => {
    setPickerAnchor(null);
    if (question.predefined && question.key === chosen.key) return;

    const nextQuestion = seededQuestion(chosen);
    const jumps = liveLogicJumps(question).length > 0;
    const apply = () => {
      setUndoable({
        previous: {
          key: question.key,
          type: question.type,
          predefined: question.predefined,
          question: question.question,
          options: question.options,
          option_bank_id: question.option_bank_id,
          restricted: question.restricted,
          secure: question.secure,
          format: question.format,
          format_validation_message: question.format_validation_message,
          min_date: question.min_date,
          max_date: question.max_date,
          logic_jumps_attributes: question.logic_jumps_attributes,
        },
        previousCustomKeyMode: customKeyMode,
        seededQuestion: nextQuestion,
        title: chosen.title,
      });
      // The key binds the answers, so a jump on the old options cannot fire.
      setTriggers({});
      onChange({
        key: chosen.key,
        type: chosen.type,
        predefined: true,
        question: nextQuestion,
        options: null,
        option_bank_id: null,
        restricted: null,
        secure: chosen.secure ?? false,
        // The definition owns a standard question's format.
        format: chosen.format ?? null,
        format_validation_message: chosen.format_validation_message ?? null,
        min_date: null,
        max_date: null,
        ...(jumps
          ? { logic_jumps_attributes: destroyedLogicJumps(question) }
          : {}),
      });
      setShowDateRange(false);
      setCustomKeyMode(false);
    };
    if (jumps) {
      requestConfirmation('changeType', apply);
    } else {
      apply();
    }
  };

  const undoBinding = () => {
    if (!undoable) return;
    const {
      previous,
      previousCustomKeyMode,
      seededQuestion: seeded,
    } = undoable;
    onChange({
      ...previous,
      question:
        question.question === seeded ? previous.question : question.question,
    });
    setTriggers(
      triggerValuesByIndex(
        { ...question, ...previous },
        previous.options ?? [],
      ),
    );
    setShowDateRange(previous.min_date != null || previous.max_date != null);
    setCustomKeyMode(previousCustomKeyMode);
    setUndoable(null);
    setRefocusKey(true);
  };

  const startCustomKey = () => {
    setUndoable(null);
    onChange({
      key: question.predefined ? '' : question.key,
      predefined: false,
      secure: false,
      options: isOptionable(question.type) ? (question.options ?? []) : null,
      ...(question.predefined ? CLEARED_VALIDATION : {}),
    });
    setCustomKeyMode(true);
    setPickerAnchor(null);
  };

  const changeType = (type: CustomType) => {
    setTriggers({});
    onChange({
      type,
      options: isOptionable(type) ? (question.options ?? []) : null,
      restricted: ALWAYS_RESTRICTED.includes(type) ? true : question.restricted,
      years_of_validation: requiresYearsOfValidation(type)
        ? (question.years_of_validation ?? MIN_YEARS_OF_VALIDATION)
        : question.years_of_validation,
      ...CLEARED_VALIDATION,
      logic_jumps_attributes: destroyedLogicJumps(question),
    });
    setShowDateRange(false);
  };

  const onTypeChange = (option: { value: CustomType }) => {
    if (option.value === question.type) return;
    if (liveLogicJumps(question).length > 0) {
      requestConfirmation('changeType', () => changeType(option.value));
    } else {
      changeType(option.value);
    }
  };

  const removeQuestion = () => {
    if (hasLogicJumps(question)) {
      requestConfirmation('removeQuestion', onRemove);
    } else {
      onRemove();
    }
  };

  const toggleValidation = () => {
    const on = typeof question.format === 'string' || showDateRange;
    if (on) {
      onChange(CLEARED_VALIDATION);
      setShowDateRange(false);
    } else if (question.type === 'datepicker') {
      setShowDateRange(true);
    } else {
      onChange({ format: '', format_validation_message: null });
    }
  };

  const pickImage = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    // Picking the same file again has to fire a change event.
    input.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setImageError(intl.formatMessage(messages.imageWrongType));
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setImageError(intl.formatMessage(messages.imageTooLarge));
      return;
    }
    setImageError(null);
    onChange({
      image: file,
      image_filename: file.name,
      image_url: null,
      remove_image: false,
    });
  };

  const removeImage = () => {
    setImageError(null);
    onChange({
      image: null,
      image_filename: null,
      image_url: null,
      remove_image: true,
    });
  };

  const options: QuestionOption[] = question.predefined
    ? templateOptions(template)
    : (question.options ?? []);

  const updateOptions = (next: QuestionOption[]) => onChange({ options: next });

  const updateOption = (index: number, patch: Partial<QuestionOption>) => {
    const next = options.map((option, i) =>
      i === index ? { ...option, ...patch } : option,
    );
    // A jump triggers on the option's value, so a renamed value renames the
    // trigger, and every trigger held back by an earlier keystroke follows.
    const synced = syncLogicJumpTriggers(
      question.logic_jumps_attributes,
      triggers,
      next,
    );
    if (synced.jumps !== question.logic_jumps_attributes) {
      setTriggers(synced.triggers);
      onChange({ options: next, logic_jumps_attributes: synced.jumps });
    } else {
      updateOptions(next);
    }
  };

  const removeOption = (index: number) => {
    const next = options.filter((_, i) => i !== index);
    const trigger = triggers[index];
    const shifted: Record<number, string> = {};
    Object.entries(triggers).forEach(([key, value]) => {
      const position = Number(key);
      if (position < index) shifted[position] = value;
      else if (position > index) shifted[position - 1] = value;
    });
    if (trigger !== undefined) {
      requestConfirmation('removeOption', () => {
        setTriggers(shifted);
        onChange({
          options: next,
          logic_jumps_attributes: removeLogicJumpValue(
            question.logic_jumps_attributes,
            trigger,
          ),
        });
      });
    } else {
      setTriggers(shifted);
      updateOptions(next);
    }
  };

  // A bank replaces the typed options a jump triggers on, in either
  // direction, so linking one drops the jumps as unlinking does.
  const changeBank = (
    kind: 'linkBank' | 'unlinkBank',
    optionBankId: number | null,
  ) => {
    const apply = () => {
      setTriggers({});
      onChange({
        option_bank_id: optionBankId,
        logic_jumps_attributes: destroyedLogicJumps(question),
      });
    };
    if (liveLogicJumps(question).length > 0) {
      requestConfirmation(kind, apply);
    } else {
      apply();
    }
  };

  const keyLabel = question.key || intl.formatMessage(messages.enterDataKey);
  const restrictedLocked = ALWAYS_RESTRICTED.includes(question.type);
  const validationOn = typeof question.format === 'string' || showDateRange;
  const hasImage = Boolean(
    question.image || question.image_url || question.image_filename,
  );

  return (
    <div
      className={cx(styles.card, { [styles.cardError]: Boolean(errors) })}
      data-testid={`question-card-${question.uid}`}
    >
      <div
        className={styles.dragHandle}
        {...dragHandleProps}
        aria-label={intl.formatMessage(messages.dragQuestion)}
      >
        <Icon icon={DragAndDropOutlineIcon} size="xs" color="inherit" />
      </div>
      <div className={styles.cardBody}>
        <InputBase
          fullWidth
          className={cx(styles.questionInput, {
            [styles.questionInputError]: Boolean(errors?.question),
          })}
          placeholder={intl.formatMessage(messages.questionPlaceholder)}
          value={question.question}
          inputProps={{
            'aria-label': intl.formatMessage(globalMessages.question),
          }}
          onChange={event => onChange({ question: event.target.value })}
        />
        {errors?.question && (
          <Typography variant="caption" className={styles.helperError}>
            {errors.question}
          </Typography>
        )}
        {showHint ? (
          <InputBase
            fullWidth
            className={styles.hintInput}
            placeholder={intl.formatMessage(messages.hintPlaceholder)}
            value={question.hint ?? ''}
            inputProps={{ 'aria-label': intl.formatMessage(messages.hint) }}
            onChange={event => onChange({ hint: event.target.value })}
          />
        ) : (
          <div>
            <ButtonBase
              className={styles.addHint}
              onClick={() => setShowHint(true)}
              disableRipple
            >
              <Typography variant="body2" color="primary">
                {intl.formatMessage(messages.addHint)}
              </Typography>
            </ButtonBase>
          </div>
        )}

        <div>
          <input
            ref={imageInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className={styles.visuallyHidden}
            tabIndex={-1}
            aria-label={intl.formatMessage(messages.questionImage)}
            onChange={pickImage}
          />
          {hasImage ? (
            <div className={styles.imageRow}>
              {question.image_url ? (
                <img
                  src={question.image_url}
                  alt={intl.formatMessage(messages.questionImage)}
                  className={styles.imageThumbnail}
                />
              ) : (
                <Icon
                  icon={PhotographOutlineIcon}
                  size="sm"
                  color="inherit"
                  className={styles.iconControl}
                />
              )}
              <Typography variant="body2" className={styles.imageName}>
                {question.image_filename ?? question.image?.name}
              </Typography>
              <IconButton
                size="small"
                className={styles.removeButton}
                aria-label={intl.formatMessage(messages.removeImage)}
                onClick={removeImage}
              >
                <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
              </IconButton>
            </div>
          ) : (
            <ButtonBase
              className={styles.addHint}
              onClick={() => imageInputRef.current?.click()}
              disableRipple
            >
              <Typography variant="body2" color="primary">
                {intl.formatMessage(messages.addImage)}
              </Typography>
            </ButtonBase>
          )}
          {imageError && (
            <Typography variant="caption" className={styles.helperError}>
              {imageError}
            </Typography>
          )}
        </div>

        <div className={styles.controlsRow}>
          <div className={styles.customKeyRow}>
            {customKeyMode ? (
              <Input
                size="small"
                rootClass={styles.keyInput}
                value={question.key}
                error={Boolean(errors?.key)}
                hideErrorMessage
                placeholder={intl.formatMessage(messages.customKeyPlaceholder)}
                inputRef={keyInputRef}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.dataKey),
                  className: styles.keyMono,
                }}
                onChange={event => onChange({ key: event.target.value })}
              />
            ) : (
              <button
                ref={keyButtonRef}
                type="button"
                className={cx(styles.keyButton, {
                  [styles.keyError]: Boolean(errors?.key),
                })}
                aria-haspopup="dialog"
                onClick={event => setPickerAnchor(event.currentTarget)}
              >
                {Boolean(question.key) && (
                  <Icon
                    icon={SearchOutlineIcon}
                    size="sm"
                    color="inherit"
                    className={styles.keyIcon}
                  />
                )}
                <span className={styles.keyMono}>{keyLabel}</span>
                {Boolean(question.key) && (
                  <Icon
                    icon={PencilOutlineIcon}
                    size="sm"
                    color="inherit"
                    className={styles.keyPencil}
                  />
                )}
              </button>
            )}
            {question.predefined && (
              <span className={styles.standardChip}>
                {intl.formatMessage(messages.standardKeyChip)}
              </span>
            )}
            {hasLogicJumps(question) && (
              <span className={styles.logicChip}>
                <Icon
                  icon={LightningBoltOutlineIcon}
                  size="xs"
                  color="inherit"
                />
                {intl.formatMessage(messages.hasLogicJumps)}
              </span>
            )}
            {customKeyMode && (
              <ButtonBase
                onClick={event => setPickerAnchor(event.currentTarget)}
                disableRipple
              >
                <Typography variant="body2" color="primary">
                  {intl.formatMessage(messages.standardKeyBadge)}
                </Typography>
              </ButtonBase>
            )}
            {errors?.key && (
              <Typography variant="caption" className={styles.helperError}>
                {errors.key}
              </Typography>
            )}
          </div>

          <div className={styles.controlsRight}>
            <CustomTooltip
              dense
              title={
                question.predefined
                  ? intl.formatMessage(messages.answerTypeLocked)
                  : ''
              }
            >
              <div className={styles.typeSelect}>
                <StyledReactSelect
                  size="small"
                  aria-label={intl.formatMessage(messages.answerType)}
                  options={typeOptions}
                  value={currentType}
                  isDisabled={question.predefined}
                  isSearchable={false}
                  onChange={onTypeChange}
                  {...typeMenuGuard}
                />
              </div>
            </CustomTooltip>
            <CustomTooltip
              dense
              title={intl.formatMessage(globalMessages.required)}
            >
              <span>
                <Checkbox
                  checked={Boolean(question.required)}
                  inputProps={{
                    'aria-label': intl.formatMessage(globalMessages.required),
                  }}
                  onChange={() => onChange({ required: !question.required })}
                />
              </span>
            </CustomTooltip>
            {canValidateAnswer(question) && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.validateAnswerDescription)}
              >
                <span>
                  <IconButton
                    size="small"
                    className={styles.restrictedButton}
                    aria-label={intl.formatMessage(messages.validateAnswer)}
                    aria-pressed={validationOn}
                    onClick={toggleValidation}
                  >
                    <Icon
                      icon={ShieldCheckOutlineIcon}
                      size="md"
                      color="inherit"
                      className={cx(styles.iconControl, {
                        [styles.iconControlOn]: validationOn,
                      })}
                    />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}
            {canConfirmAnswer(question) && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.confirmAnswerDescription)}
              >
                <span>
                  <IconButton
                    size="small"
                    className={styles.restrictedButton}
                    aria-label={intl.formatMessage(messages.confirmAnswer)}
                    aria-pressed={Boolean(question.confirm)}
                    onClick={() => onChange({ confirm: !question.confirm })}
                  >
                    <Icon
                      icon={ClipboardCheckOutlineIcon}
                      size="md"
                      color="inherit"
                      className={cx(styles.iconControl, {
                        [styles.iconControlOn]: Boolean(question.confirm),
                      })}
                    />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}
            {question.predefined && question.secure && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.secureDescription)}
              >
                <Icon
                  icon={LockClosedOutlineIcon}
                  size="md"
                  color="inherit"
                  role="img"
                  className={styles.iconControl}
                  aria-label={intl.formatMessage(messages.secureDescription)}
                />
              </CustomTooltip>
            )}
            {!question.predefined && canRestrict && (
              <CustomTooltip
                dense
                title={intl.formatMessage(messages.restrictedDescription)}
              >
                <span>
                  <IconButton
                    size="small"
                    className={styles.restrictedButton}
                    aria-label={intl.formatMessage(messages.restricted)}
                    aria-pressed={
                      Boolean(question.restricted) || restrictedLocked
                    }
                    disabled={restrictedLocked}
                    onClick={() =>
                      onChange({ restricted: !question.restricted })
                    }
                  >
                    <Icon
                      icon={LockClosedOutlineIcon}
                      size="md"
                      color="inherit"
                      className={cx(styles.iconControl, {
                        [styles.iconControlOn]:
                          Boolean(question.restricted) || restrictedLocked,
                      })}
                    />
                  </IconButton>
                </span>
              </CustomTooltip>
            )}
            <IconButton
              size="small"
              className={styles.removeButton}
              aria-label={intl.formatMessage(messages.removeQuestion)}
              onClick={removeQuestion}
            >
              <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
            </IconButton>
          </div>
        </div>

        {/* Mounted before its text: a live region that arrives with its
            content is often not announced. */}
        <span role="status" className={styles.visuallyHidden}>
          {undoable &&
            intl.formatMessage(messages.standardKeyApplied, {
              title: undoable.title,
            })}
        </span>
        {undoable && (
          <Alert type="informational" fullWidth>
            <span className={styles.undoBar}>
              <span aria-hidden="true">
                {intl.formatMessage(messages.standardKeyApplied, {
                  title: undoable.title,
                })}
              </span>
              <ButtonBase onClick={undoBinding} disableRipple>
                <Typography variant="body2" color="primary" component="span">
                  {intl.formatMessage(messages.undoStandardKey)}
                </Typography>
              </ButtonBase>
            </span>
          </Alert>
        )}

        {showsFormat(question, settings) && (
          <div>
            <div className={styles.validationRow}>
              <Input
                size="small"
                rootClass={styles.validationInput}
                value={question.format ?? ''}
                disabled={question.predefined}
                error={Boolean(errors?.format)}
                hideErrorMessage
                placeholder={intl.formatMessage(
                  messages.answerFormatPlaceholder,
                )}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.answerFormat),
                  className: styles.keyMono,
                }}
                onChange={event => onChange({ format: event.target.value })}
              />
              <Input
                size="small"
                rootClass={styles.validationInput}
                value={question.format_validation_message ?? ''}
                disabled={question.predefined}
                placeholder={intl.formatMessage(messages.formatMessage)}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.formatMessage),
                }}
                onChange={event =>
                  onChange({ format_validation_message: event.target.value })
                }
              />
              {!question.predefined && (
                <IconButton
                  size="small"
                  className={styles.removeButton}
                  aria-label={intl.formatMessage(messages.removeValidation)}
                  onClick={toggleValidation}
                >
                  <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
                </IconButton>
              )}
            </div>
            {errors?.format && (
              <Typography variant="caption" className={styles.helperError}>
                {errors.format}
              </Typography>
            )}
            {question.predefined && (
              <Typography variant="caption" color="textSecondary">
                {intl.formatMessage(messages.answerFormatLocked)}
              </Typography>
            )}
          </div>
        )}

        {question.type === 'datepicker' && showDateRange && (
          <div>
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(messages.dateRangeDescription)}
            </Typography>
            <div className={styles.validationRow}>
              <Input
                size="small"
                type="date"
                rootClass={styles.validationInput}
                label={intl.formatMessage(messages.minDate)}
                value={question.min_date ?? ''}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.minDate),
                }}
                onChange={event =>
                  onChange({ min_date: event.target.value || null })
                }
              />
              <Input
                size="small"
                type="date"
                rootClass={styles.validationInput}
                label={intl.formatMessage(messages.maxDate)}
                value={question.max_date ?? ''}
                inputProps={{
                  'aria-label': intl.formatMessage(messages.maxDate),
                }}
                onChange={event =>
                  onChange({ max_date: event.target.value || null })
                }
              />
            </div>
            {errors?.dateRange && (
              <Typography variant="caption" className={styles.helperError}>
                {errors.dateRange}
              </Typography>
            )}
          </div>
        )}

        {question.type === 'hidden_field' && (
          <Input
            size="small"
            fullWidth
            label={intl.formatMessage(messages.hiddenValue)}
            value={question.hidden_value ?? ''}
            error={errors?.hiddenValue}
            inputProps={{
              'aria-label': intl.formatMessage(messages.hiddenValue),
            }}
            onChange={event => onChange({ hidden_value: event.target.value })}
          />
        )}

        {requiresYearsOfValidation(question.type) && (
          <Input
            size="small"
            type="number"
            rootClass={styles.validationInput}
            label={intl.formatMessage(messages.yearsOfValidation)}
            value={question.years_of_validation ?? ''}
            error={errors?.yearsOfValidation}
            inputProps={{
              'aria-label': intl.formatMessage(messages.yearsOfValidation),
              min: MIN_YEARS_OF_VALIDATION,
              max: MAX_YEARS_OF_VALIDATION,
            }}
            onChange={event =>
              onChange({
                years_of_validation:
                  event.target.value === '' ? null : Number(event.target.value),
              })
            }
          />
        )}

        {isOptionable(question.type) && (
          <OptionsEditor
            options={options}
            readOnly={Boolean(question.predefined)}
            error={errors?.options}
            optionBanks={optionBanks}
            optionBankId={question.option_bank_id}
            onAddOption={() =>
              updateOptions([...options, { value: '', visible: true }])
            }
            onUpdateOption={updateOption}
            onRemoveOption={removeOption}
            onLinkBank={id => changeBank('linkBank', id)}
            onUnlinkBank={() => changeBank('unlinkBank', null)}
          />
        )}
      </div>

      <StandardKeyPicker
        anchorEl={pickerAnchor}
        templates={templates}
        usedKeys={usedKeys.filter(key => key !== question.key)}
        onSelect={applyTemplate}
        onCreateCustomKey={startCustomKey}
        onClose={() => setPickerAnchor(null)}
      />
    </div>
  );
};
